<template>
  <!-- showProp, -->
  <div
    :class="[
      'rtc',
      { rtc_dimmed: dimmed },
      { rtc_empty: asEmpty },
      { rtc_folded: !range },
      { rtc_active: active && range && !asEmpty },
      { rtc_locked: isLocked },
      { rtc_centered: isCentered && !active },
      { rtc_graph_bordered: viewProps.combos_columns_line },
      { rtc_graph_columns: showComboGraph },
      `rtc_${showProp}_${rangeKey}`,
    ]"
    :style="contentStyle"
    @click="handleClick"
    @mouseenter="handleMouseenter"
    @mousedown="handleMousedown"
  >
    <!-- keep html order of value>title for small-screen styles -->
    <!-- {{ valueProp }} -->

    <div
      v-if="
        !showLegend &&
        !displayEmpty &&
        (noValue && !active ? false : true) &&
        (isCentered ? active : !isCentered)
      "
      class="rtc_value"
      v-html="
        valueProp === 'ev'
          ? getEvHtml({
              ...bettingInfo,
              ignoreIcmEvIntercept: true,
              ev: combosPlayer?.[valueProp],
              player: player,
            })
          : numFixedUnit(
              combosPlayer?.[valueProp] * (valueProp === 'eq' || valueProp === 'eqr' ? 100 : 1),
              valueProp,
            )
      "
    ></div>

    <div
      v-if="active ? true : !noTitle"
      class="rtc_title"
      @click.stop="e => handleClick(e, 'title')"
      @mousedown.stop="e => handleMousedown(e, 'title')"
    >
      {{ combos.id }}
      <i
        v-if="isLocked && range && !asEmpty"
        class="rtc_title_lock mdi mdi-lock"
      ></i>
    </div>
    <div
      v-for="col in graphCombosColumns"
      :id="col.id"
      :key="col.id"
      class="rtc_graph_column rtc_graph_column_bordered"
      :style="col.clrs"
    />

    <div
      v-if="showLegend"
      class="rtc_graph_legend no-scroll-track"
    >
      <div
        v-for="(action, idx) in combosSolutionLegend"
        :key="`${action.code}_${idx}`"
        class="rtc_graph_legend_item"
        :data-tst="`rtc_action_${idx}`"
      >
        <span>
          {{ action.display_name || $t(`browse.${action.group_display_name}`) }}
          <span
            class="htc_graph_legend_item_bet"
            v-html="
              getBetHtml({
                showBetsIn,
                a: action,
                pot_css_class: 'bet-as-pot bet-as-pot-rtc',
              })
            "
          ></span>
        </span>
        <span>
          {{ numFixedUnit(action.strategy * 100, 'strategy') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getEvClr,
  getComboEvCompareClr,
  getStrategyClrs,
  getEvClrs,
  getEvCompareClrs,
  getEqClrs,
  getEqrClrs,
  getBlockerClrs,
  getComboStrategyClrs,
} from '@/helpers/solution'
import { getGroupedActions } from '@/helpers/grouping'

import format_mix from '@/mixins/format_mix'

export default {
  name: 'RangeTableCell', // rtc
  mixins: [format_mix],
  props: {
    combos: {
      default: () => ({ combos: [] }),
    },
    textStyle: {
      default: () => 'one',
    },
    combosPlayer: Object,
    active: Boolean,
    asEmpty: Boolean,

    hasDimmed: { type: Boolean, default: true },
    isDimmed: Boolean,
    noValue: Boolean,
    noTitle: Boolean,
    player: String,
    showProp: String,
    valueProp: String,
    isCentered: Boolean,
    useHex: Boolean,
    grouping: String,
    showBetsIn: String,
    bettingInfo: Object,
    isLocked: Boolean,

    hasIcon: Boolean,
    icon: { default: 'mdi-pin-outline' },

    filterEvCompare: Object,

    viewProps: { default: () => ({}) },
    filterCombos: { default: () => ({ actions: [] }) },
    evProps: { default: () => ({}) },

    rangeKey: { default: () => 'range' },
    range: { default: () => 1 },
    hasSuitInTitle: Boolean,
  },
  emits: ['click', 'mouseenter', 'mousedown', 'icon:click'],
  methods: {
    handleClick(e, trigger = 'rtc') {
      this.$emit('click', {
        e,
        combos: this.combos,
        disabled: this.asEmpty || !this.range,
        trigger,
      })
    },
    handleMouseenter(e, trigger = 'rtc') {
      this.$emit('mouseenter', {
        e,
        combos: this.combos,
        disabled: this.asEmpty || !this.range,
        trigger,
      })
    },
    handleMousedown(e, trigger = 'rtc') {
      this.$emit('mousedown', {
        e,
        combos: this.combos,
        disabled: this.asEmpty || !this.range,
        trigger,
      })
    },
    getCombosInRange() {
      return this.combos.combos.filter(
        combo => combo.players?.[this.player]?.[this.rangeKey || 'range'] > 0,
      )
    },
  },
  computed: {
    showLegend() {
      return this.active && !this.displayEmpty && this.showProp === 'strategy'
    },
    combosSolutionLegend() {
      let actions = this.combosSolution
      if (this.grouping === 'swv_grouping_4') {
        const res = getGroupedActions({
          actions,
          grouping: this.grouping,
          groupValues: ['strategy'],
          raisesFirst: true,
        })
        actions = res
      } else if (this.grouping === 'swv_grouping_all') {
        actions = this.groupedBets
      }
      return actions
    },
    groupedBets() {
      const actions = this.combosSolution
      const res = getGroupedActions({
        actions,
        grouping: 'swv_grouping_all',
        groupValues: ['strategy'],
        raisesFirst: true,
      })
      return res
    },
    graphCombosColumns() {
      if (!this.showComboGraph || this.displayEmpty) {
        return []
      }
      if (this.showProp === 'range') {
        const columns = this.combosColumns.map(col => {
          const p = col.localRange * 100
          const clrs = col.localRange
            ? `background-image: linear-gradient(to top, var(--clr-range) ${p}%, var(--clr-rtc-back) ${p}%)`
            : ''
          return {
            id: col.id,
            clrs,
          }
        })
        return columns
      }
      if (this.showProp === 'eq' || this.showProp === 'eqr') {
        const showProp = this.showProp === 'eq' ? 'eq' : 'eqr_clrscaled'
        const columns = this.combosColumns.map(col => {
          const p = col.localRange * 100
          const l_player = col.players[this.player]
          const gradient = Math.round((l_player[showProp] * 100) / 5) * 5
          const clr = `--clr-eveqeqrbl${gradient}`
          const clrs = col.localRange
            ? `background-image: linear-gradient(to top, var(${clr}) ${p}%, var(--clr-rtc-back) ${p}%)`
            : ''
          return {
            id: col.id,
            clrs,
          }
        })
        return columns
      }
      if (
        this.showProp === 'ev' ||
        (this.filterEvCompare?.disabled && this.showProp === 'ev_compare')
      ) {
        const columns = this.combosColumns.map(col => {
          const p = col.localRange * 100
          const totalEv = this.evProps[`${this.player}_total_ev`]

          const l_player = col.players[this.player]
          const clr = getEvClr({ total_ev: totalEv, ev: l_player.ev, clrScale: 10 })
          const clrs = col.localRange
            ? `background-image: linear-gradient(to top, var(${clr}) ${p}%, var(--clr-rtc-back) ${p}%)`
            : ''
          return {
            id: col.id,
            clrs,
          }
        })
        return columns
      }
      if (this.showProp === 'ev_compare') {
        const { pot, action_primary, action_secondary, compare_by_best_ev_action } =
          this.filterEvCompare

        const columns = this.combosColumns.map(col => {
          const p = col.localRange * 100

          const clr = getComboEvCompareClr({
            combo: col,
            grouping: this.grouping,
            action_primary,
            action_secondary,
            compare_by_best_ev_action,
            pot,
            player: this.player,
          })

          const clrs = col.localRange
            ? `background-image: linear-gradient(to top, var(${clr}) ${p}%, var(--clr-rtc-back) ${p}%)`
            : ''
          return {
            id: col.id,
            clrs,
          }
        })
        return columns
      }
      const columns = this.combosColumns.map(col => {
        const r = getComboStrategyClrs({
          solution: col.localSolution,
          strategyKey: 'localStrategy',
          range: col.solutionRange,
        })
        const clrs = this.useHex ? r.hex : r.vars
        return {
          id: col.id,
          clrs,
        }
      })
      return columns
    },
    combosSolution() {
      if (this.showProp === 'ev_compare') {
        return this.combo.solution
      }
      if (this.filterCombos.actions.length) {
        const res = this.combos.solution.filter(i =>
          this.filterCombos.actions.some(a => a.code === i.code),
        )
        return res
      }
      return this.combos.solution
    },
    combosColumns() {
      const filterActionsLength = this.filterCombos.actions.length
      const localCombos = this.combos.combos.map(cp => {
        const getRanges = () => {
          const player = cp.players?.[this.player]
          if (!player.range) {
            return { range: 0, solutionRange: 0 }
          }
          if (this.viewProps.study_range_full_height) {
            return { range: 1, solutionRange: 1 }
          }
          if (this.viewProps.study_range_normalized) {
            return {
              range: player.range_normalized,
              solutionRange: player.default.range_normalized,
            }
          }
          return { range: player.range, solutionRange: player.default.range }
        }

        const { range, solutionRange } = getRanges()

        const solution = (cp.solution || [])
          .map(action => {
            let { strategy } = action
            if (filterActionsLength) {
              const infilterActions = this.filterCombos.actions.some(a => a.code === action.code)
              strategy = infilterActions ? action.strategy : 0
            }
            const actionRes = {
              ...action,
              localStrategy: strategy,
            }
            return actionRes
          })
          .filter(c => c.localStrategy)

        const res = {
          ...cp,
          localRange: range,
          solutionRange,
          localSolution: solution,
        }
        return res
      })
      return localCombos
    },
    displayEmpty() {
      return this.asEmpty || !this.range
    },
    raHeight() {
      return `${this.range * 100}%`
    },
    contentStyle() {
      if (this.displayEmpty) return ''
      if (this.viewProps.study_combos_columns) return ''
      if (
        this.showProp === 'strategy' ||
        this.showProp === 'strategy_ev' ||
        this.showProp === 'strategy_eq'
      ) {
        // if (this.viewProps.study_combos_columns) return ''
        return this.strClrs
      }
      if (this.showProp === 'ev_compare') {
        return this.evCompareClrs
      }
      if (this.showProp === 'ev') {
        return this.evClrs
      }
      if (this.showProp === 'eq') {
        return this.eqClrs
      }
      if (this.showProp === 'eqr') {
        return this.eqrClrs
      }
      if (this.showProp === 'range' && !this.viewProps.study_combos_columns) {
        return this.rangeClrs
      }
      return ''
    },
    rangeClrs() {
      if (this.viewProps.study_range_full_height) {
        return `--opacity-range: ${this.combosPlayer.range};`
      }
      return `background-image: linear-gradient(var(--clr-range),var(--clr-range)); background-size: 100% ${this.raHeight}`
    },

    strClrs() {
      const solution = this.combosSolution.filter(i => i.strategy)
      const { clrs_var, clrs_hex, size } = getStrategyClrs({
        solution,
        range: this.range,
      })
      const clrs = this.useHex ? clrs_hex : clrs_var
      const style = `background-image: ${clrs}; background-size:${size};`
      return style
    },
    evCompareClrs() {
      if (this.filterEvCompare.disabled) {
        return this.evClrs
      }
      const { clrs, size } = getEvCompareClrs({
        combos_in_range: this.getCombosInRange(),
        study_ev_comparison: this.filterEvCompare,
        range: this.range,
        grouping: this.grouping,
      })
      const style = `background-image: ${clrs} ; background-size:${size}`
      return style
    },
    evClrs() {
      const totalEv = this.evProps[`${this.player}_total_ev`]

      const { clrs, size } = getEvClrs({
        total_ev: totalEv,
        combos_in_range: this.getCombosInRange(),
        player: this.player,
        range: this.range,
      })
      const style = `background-image: ${clrs} ; background-size:${size}`
      return style
    },
    eqClrs() {
      const { clrs, size } = getEqClrs({
        combos_in_range: this.getCombosInRange(),
        player: this.player,
        range: this.range,
      })
      const style = `background-image: ${clrs} ; background-size:${size}`
      return style
    },
    eqrClrs() {
      const { clrs, size } = getEqrClrs({
        combos_in_range: this.getCombosInRange(),
        player: this.player,
        range: this.range,
      })
      const style = `background-image: ${clrs} ; background-size:${size}`
      return style
    },
    blockerClrs() {
      const { clrs, size } = getBlockerClrs({
        combos_in_range: this.getCombosInRange(),
        player: this.player,
        range: this.range,
      })
      const style = `background-image: ${clrs} ; background-size:${size}`
      return style
    },
    showComboGraph() {
      return this.viewProps.study_combos_columns
    },
    dimmed() {
      if (!this.hasDimmed || this.displayEmpty) {
        return false
      }
      if (this.isDimmed) {
        return true
      }
      return false
    },
  },
}
</script>

<style lang="scss" scoped>
@media (hover: hover) and (pointer: fine) {
  .rtc {
    &:hover {
      &:not(.rtc_folded, .rtc_empty) {
        cursor: pointer;
        .rtc_icon {
          opacity: 1;
        }
        &:not(.rtc_active, .rtc_diss-hover-dimm) {
          &:after {
            z-index: 0;
            opacity: 0.3;
          }
        }
        &.rtc_dimmed:not(.rtc_active) {
          opacity: 1 !important;
          &:before {
            background: transparent !important;
          }
        }
      }
    }
  }
}

.rtc {
  background-repeat: no-repeat;
  background-position-y: 100%;
  display: flex;
  overflow: hidden;
  position: relative;
  z-index: 0;

  color: var(--clr-range_text);
  text-shadow: var(--range-cell-text-shadow);

  // --rtc-min-height: 2rem;
  --rtc-min-height: 0rem;

  --rtc-font-size: clamp(6px, 1.25rem, clamp(1px, 2.75vw, 2vh));
  --rtc-value-font-size: clamp(6px, 1rem, clamp(1px, 2.75vw, 2vh));
  --rtc-lock-font-size: clamp(6px, 1rem, clamp(1px, 2.75vw, 2vh));

  --rtc-centered-default-font-size: clamp(6px, 0.875rem, clamp(1px, 2.25vw, 2vh));
  --rtc-centered-5x-font-size: clamp(6px, 0.875rem, clamp(1px, 0.65vw, 2vh));
  --rtc-centered-mobile-font-size: clamp(6px, 0.875rem, clamp(1px, 1.5vw, 2vh));
  --rtc-centered-font-size: var(--rtc-centered-default-font-size);

  --rtc-active-font-size: clamp(6px, 1.5rem, clamp(1px, 3.75vw, 3vh));
  --rtc-active-value-font-size: clamp(6px, 1.25rem, clamp(1px, 3.75vw, 3vh));

  --clr-rtc-back: var(--clr-range_active);
  --before-opacity: 1;

  // font-weight: 500;

  background-color: var(--clr-rtc-back);
  flex-shrink: 0;
  min-height: var(--rtc-min-height);
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  will-change: height, width;

  &:after {
    content: '';
    background: black;
    height: 100%;
    width: 100%;
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
    opacity: 0;
  }
  &_dimmed:not(.rtc_active) {
    &:after {
      opacity: 0.55;
      transition: 0.2s;
    }
  }

  // rouded table
  &:not(.rtc_active) {
    --rtc-radius: 4px;
    &:nth-child(1) {
      border-radius: var(--rtc-radius) 0 0 0;
    }
    &:nth-child(13) {
      border-radius: 0 var(--rtc-radius) 0 0;
    }
    &:nth-child(157) {
      border-radius: 0 0 0 var(--rtc-radius);
    }
    &:nth-child(169) {
      border-radius: 0 0 var(--rtc-radius) 0;
    }
  }

  --active-size-width: 300%;
  --active-size-height: 300%;
  @keyframes maximizeRtc {
    0% {
      height: 100%;
      width: 100%;
      top: 0px;
      left: 0px;
    }
    100% {
      height: var(--active-size-height);
      width: var(--active-size-width);
    }
  }

  --rtc-close-inset: 0rem auto auto -3rem;
  &:nth-child(13n + 1) {
    --rtc-close-inset: 0rem -3rem auto auto;
  }
  &_close {
    position: absolute;
    inset: var(--rtc-close-inset);
    font-size: 1.75rem;
    padding: 0.25rem;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.44);
  }
  &_active {
    --rtc-font-size: var(--rtc-active-font-size);
    --rtc-value-font-size: var(--rtc-active-value-font-size);

    height: var(--active-size-height);
    width: var(--active-size-width);
    outline: none !important;
    box-shadow:
      0px 0px 0px 4px var(--clr-primary),
      0px 0px 16px 0px rgba(0, 0, 0, 0.85);

    z-index: 2 !important;
    border-radius: 4px;
    top: 0px;
    left: 0px;

    // keyframe is used animation have no animated resize
    // switching strategy and ranges tab
    animation: maximizeRtc 0.15s ease-in-out;

    // 3x + 5x - top left 4px
    &:nth-child(n + 1):nth-child(-n + 13) {
      top: 4px;
    }
    &:nth-child(13n + 1) {
      left: 4px;
    }

    // 3x
    &:nth-child(13n) {
      left: 4px;
    }
    &:nth-child(13n + 12) {
      left: calc(-100% - 4px);
    }
    &:nth-child(13n + 13) {
      left: calc(-200% - 4px);
    }
    &:nth-child(n + 157):nth-child(-n + 169) {
      top: calc(-200% - 4px);
    }
    &:nth-child(n + 144):nth-child(-n + 156) {
      top: calc(-100% - 4px);
    }

    .rtc_icon {
      opacity: 1;
      &::before {
        content: '\F0930';
      }
    }
    &:not(.rtc_graph_columns) {
      &:after {
        content: '';
        background-color: var(--clr-rtc-back);
        position: absolute;
        height: 100%;
        width: 100%;
        z-index: -1;
      }
    }
  }
  &_range_range_full_height {
    &:not(.rtc_graph_columns) {
      &:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: var(--opacity-range);
        background-color: var(--clr-range);
      }
      &.rtc_empty {
        &:after {
          content: '';
          background-color: var(--clr-rtc-back);
          position: absolute;
          height: 100%;
          width: 100%;
          z-index: 0;
        }
      }
    }
  }
  &_grey:not(.rtc_empty) {
    background-color: var(--clr-black-3);
  }
  &_folded {
    --clr-rtc-back: var(--clr-range_empty);
    --opacity-range: 0;
    .rtc_title {
      opacity: 0.225;
    }
  }
  &_empty {
    --clr-rtc-back: var(--clr-black-1);
    --opacity-range: 0;
    .rtc_title {
      opacity: 0.225;
    }
  }

  --text-margin: 4px;
  &_title {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    font-size: var(--rtc-font-size);
    z-index: 1;
    top: 0;
    left: var(--text-margin);
    gap: 0.15em;
    &_lock {
      font-size: calc(var(--rtc-font-size) * 0.6);
    }
  }
  &_centered {
    --rtc-font-size: var(--rtc-centered-font-size);
    .rtc_title {
      font-size: var(--rtc-centered-font-size);
      height: 100%;
      width: 100%;
      display: flex;
      line-height: 1;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      left: 0 !important;
      right: 0 !important;
      &_lock {
        margin: 0;
      }
    }
  }
  &_value {
    position: absolute;
    font-size: var(--rtc-value-font-size);
    z-index: 1;
    line-height: 1;
    text-align: right;
    bottom: calc(var(--rtc-value-font-size) * 0.25);
    right: var(--text-margin);
  }
  &_graph {
    &_bordered {
      .rtc_graph_column_bordered {
        --clr-rtc-graph-divider: var(--clr-black-4);
        border-right: 0.5px solid var(--clr-rtc-graph-divider); //  0.01 is for firefox win
        box-sizing: content-box;
        &:last-child {
          border-right: none;
        }
      }
    }
    &_column {
      width: 100%;
      height: 100%;
    }
    &_legend {
      margin-top: auto;
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      z-index: 1;
      overflow-x: hidden;
      overflow-y: auto;
      max-height: calc(100% - var(--rtc-font-size) - 0.5rem);
      color: var(--clr-range_text);

      &_item {
        display: flex;
        justify-content: space-between;
        padding: 0 0.25rem;
        font-weight: 400;
        white-space: nowrap;
        position: relative;
        font-size: 1rem;
        line-height: 1rem;
        font-size: var(--rtc-value-font-size);
        line-height: var(--rtc-value-font-size);
        height: var(--rtc-value-font-size);
      }
    }
  }
}

@media (max-width: 1023px) {
  .rtc {
    --rtc-min-height: 0.5rem;
    &_active {
      --active-size-width: 600%;
      --active-size-height: 600%;
      &:nth-child(13n) {
        left: calc(-500% - 4px);
      }
      &:nth-child(13n + 12) {
        left: calc(-400% - 4px);
      }
      &:nth-child(13n + 11) {
        left: calc(-300% - 4px);
      }
      &:nth-child(13n + 10) {
        left: calc(-200% - 4px);
      }
      &:nth-child(13n + 9) {
        left: calc(-100% - 4px);
      }
      &:nth-child(n + 157):nth-child(-n + 169) {
        top: calc(-500% - 4px);
      }
      &:nth-child(n + 144):nth-child(-n + 156) {
        top: calc(-400% - 4px);
      }
      &:nth-child(n + 131):nth-child(-n + 143) {
        top: calc(-300% - 4px);
      }
      &:nth-child(n + 118):nth-child(-n + 130) {
        top: calc(-200% - 4px);
      }
      &:nth-child(n + 105):nth-child(-n + 117) {
        top: calc(-100% - 4px);
      }
    }
    &:not(.rtc_active) {
      .rtc_value,
      .rtc_title {
        height: 100%;
        width: 100%;
        display: flex;
        justify-content: center;
        flex-direction: column;
        line-height: 1;
        left: 0 !important;
        right: 0 !important;
      }
      .rtc_value {
        line-height: 1rem;
        height: 50%;
        align-items: flex-start;
      }
      .rtc_title {
        height: 100%;
        align-items: center;
        &_lock {
          margin: 0;
        }
      }
      // if there is no value, center title
      .rtc_value ~ .rtc_title {
        line-height: 1rem;
        height: 50%;
        align-items: center;
      }
    }
  }
}
@media (max-height: 820px) and (max-width: 1023px) {
  .rtc {
    &:not(.rtc_active) {
      .rtc_value {
        display: none !important;
      }
      .rtc_title {
        height: 100% !important;
        align-items: center !important;
        text-align: center;
        flex-direction: column;
        line-height: 1;
        &_lock {
          margin: 0;
        }
      }
    }
  }
}
</style>
